'use client';

import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';
import { AddTaskSurface, trackAddTaskInfoLinkClick } from './tracking';

/** zmeu's own site, where the full story lives. */
export const ZMEU_SITE_URL = 'https://zmeu.app';

interface AddTaskInfoProps {
  surface: AddTaskSurface;
  className?: string;
}

/**
 * The "what is zmeu" banner behind the Task label in the add-job menus.
 *
 * zmeu is a separate product from bestjobs, so the Task entry needs a sentence of
 * context before someone calls or writes: the icons alone say "phone" and "WhatsApp",
 * not what happens afterwards. Shared by the desktop navbar menu and the mobile
 * bottom bar so the explanation cannot drift between the two.
 *
 * The copy carries defaultValue fallbacks, so the banner reads correctly before the
 * `label.zmeu.info.*` keys land in Localise.
 */
export const AddTaskInfo = ({ surface, className }: AddTaskInfoProps) => {
  const { t } = useTranslation('common');

  return (
    // whitespace-normal: the menu rows are nowrap, and the description has to wrap.
    <div className={cn('w-64 whitespace-normal border-t border-ink/5 bg-surface-50 px-4 py-3', className)}>
      <p className="text-sm font-semibold text-ink">
        {t('label.zmeu.info.title', { defaultValue: 'Ce este Zmeu?' })}
      </p>
      <p className="mt-1 text-xs font-normal leading-snug text-ink-medium">
        {t('label.zmeu.info.description', {
          defaultValue: 'Zmeu îți găsește meseriașul de care ai nevoie. Ne spui ce ai de făcut, '
            + 'la telefon sau pe WhatsApp, iar noi ducem cererea meseriașilor din zona ta.',
        })}
      </p>
      <p className="mt-2 text-xs font-normal text-ink-medium">
        {t('label.zmeu.info.more', { defaultValue: 'Mai multe informații:' })}
        {' '}
        <a
          href={ZMEU_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="nav-add-task-info-link"
          onClick={() => trackAddTaskInfoLinkClick(surface)}
          className="font-semibold text-primary hover:underline"
        >
          zmeu.app
        </a>
      </p>
    </div>
  );
};
